import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Input, Alert } from 'reactstrap'
import gql from 'graphql-tag'
import Select from 'react-select'
import AttachmentViewer from "../../Drivers/Interactions/AttachmentViewer"
import { getFileIconClass, isImage } from "../../Material/DocumentModal/utils"
import "./UploadIncidentDocumentModal.css"

const AllIncidentDocumentTypes = gql`
  query AllIncidentDocumentTypes($category: String) {
    allIncidentDocumentTypes(category: $category, isActive: true) {
      edges {
        node {
          id
          name
          category
        }
      }
    }
  }
`

const IncidentDocumentTargetsQuery = gql`
  query IncidentDocumentTargetsQuery($incidentId: ID!) {
    incident(incidentId: $incidentId) {
      id
      documentSlug
    }
    allIncidentParties(incidentId: $incidentId, isArchived: false) {
      edges {
        node {
          id
          pk
          partyName
          partyType
          documentSlug
        }
      }
    }
    allLiabilityClaims(incidentId: $incidentId, isArchived: false) {
      edges {
        node {
          id
          pk
          externalId
          tpa {
            name
          }
          documentSlug
        }
      }
    }
    allSubrogationClaims(incidentId: $incidentId, isArchived: false) {
      edges {
        node {
          id
          pk
          externalId
          tpa {
            name
          }
          documentSlug
        }
      }
    }
  }
`

const ClaimTypeEnumQuery = gql`
  query ClaimTypeEnumQuery {
    claimTypeChoices: __type(name: "IncidentsTPAConfigClaimTypeChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const CreateBulkIncidentDocument = gql`
  mutation CreateBulkIncidentDocument($input: CreateBulkIncidentDocumentMutationInput!) {
    createBulkIncidentDocument(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const CreateIncidentDocument = gql`
  mutation CreateIncidentDocument($input: CreateIncidentDocumentMutationInput!) {
    createIncidentDocument(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const GenerateGcsUploadUrl = gql`
  mutation GenerateGcsUploadUrl($input: GenerateGCSUploadURLInput!) {
    generateGcsUploadUrl(input: $input) {
      ok
      uploadUrl
      blobPath
      errors {
        field
        messages
      }
    }
  }
`

const UploadIncidentDocumentModal = ({ incidentId, isOpen, toggle, refetchQuery, documentCategory, slug, requireTargetSelection = false, }) => {
    const [selectedTarget, setSelectedTarget] = useState(null)
    const [selectedClaimType, setSelectedClaimType] = useState(null)
    const isClaimCategory = documentCategory === "CLAIM"
    const { data: claimTypeData, loading: claimTypeLoading } = useQuery(ClaimTypeEnumQuery, {
        skip: !isOpen || !isClaimCategory,
    })
    const claimTypeOptions = useMemo(() => (
        claimTypeData?.claimTypeChoices?.enumValues?.map((opt) => ({
            value: opt.name,
            label: opt.description || opt.name,
        })) || []
    ), [claimTypeData])
    const documentTypeCategory = isClaimCategory ? selectedClaimType?.value : documentCategory
    const { data: incidentDocumentTypes, loading: typesLoading } = useQuery(AllIncidentDocumentTypes, {
        variables: { category: documentTypeCategory },
        skip: !isOpen || !documentTypeCategory,
    })
    const { data: incidentTargetsData, loading: targetsLoading } = useQuery(IncidentDocumentTargetsQuery, {
        variables: { incidentId },
        skip: !isOpen,
    })

    const [createBulkIncidentDocument] = useMutation(CreateBulkIncidentDocument)
    const [createIncidentDocument] = useMutation(CreateIncidentDocument)
    const [generateGcsUploadUrl] = useMutation(GenerateGcsUploadUrl)

    const [selectedFiles, setSelectedFiles] = useState([])
    const [notes, setNotes] = useState('')
    const [error, setError] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isDragActive, setIsDragActive] = useState(false)
    const [previewAttachment, setPreviewAttachment] = useState(null)

    const fileInputRef = useRef(null)
    const incidentSlug = slug || incidentTargetsData?.incident?.documentSlug || null
    const targetOptionsByCategory = useMemo(() => ({
        PARTY: incidentTargetsData?.allIncidentParties?.edges?.map(({ node }) => ({
            value: node.documentSlug,
            label: `${node.pk || node.id} - ${node.partyName || "Unknown Party"} - ${node.partyType || "Unknown"}`,
        })) || [],
        LIABILITY: incidentTargetsData?.allLiabilityClaims?.edges?.map(({ node }) => ({
            value: node.documentSlug,
            label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
        })) || [],
        SUBROGATION: incidentTargetsData?.allSubrogationClaims?.edges?.map(({ node }) => ({
            value: node.documentSlug,
            label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
        })) || [],
    }), [incidentTargetsData])

    const needsTargetSelection = requireTargetSelection && ["PARTY", "CLAIM"].includes(documentCategory)
    const targetCategoryKey = documentCategory === "CLAIM" ? selectedClaimType?.value : documentCategory
    const targetOptions = targetOptionsByCategory[targetCategoryKey] || []
    const resolvedSlug = needsTargetSelection ? selectedTarget?.value : incidentSlug
    const documentTypeOptions = useMemo(
        () => {
            if (!documentTypeCategory) return []
            return (incidentDocumentTypes?.allIncidentDocumentTypes?.edges || [])
                .map(({ node }) => ({ value: node.id, label: node.name }))
        },
        [incidentDocumentTypes, documentTypeCategory]
    )

    useEffect(() => {
        if (!documentTypeOptions.length) return
        const validTypeIds = new Set(documentTypeOptions.map((option) => option.value))
        setSelectedFiles((prev) => {
            let hasInvalidSelection = false
            const next = prev.map((file) => {
                if (!file.documentTypeId || validTypeIds.has(file.documentTypeId)) {
                    return file
                }
                hasInvalidSelection = true
                return { ...file, documentTypeId: null }
            })
            return hasInvalidSelection ? next : prev
        })
    }, [documentTypeOptions])

    const clearAllFiles = () => {
        if (previewAttachment?.isTempUrl && previewAttachment?.url) {
            URL.revokeObjectURL(previewAttachment.url)
        }
        setPreviewAttachment(null)
        setSelectedFiles((currentFiles) => {
            currentFiles.forEach((file) => {
                if (file.isVideo && file.preview?.startsWith('blob:')) {
                    URL.revokeObjectURL(file.preview)
                }
            })
            return []
        })
    }

    const processFiles = (files) => {
        if (!files?.length) return

        let completed = 0
        const preparedFiles = new Array(files.length)

        const finalize = () => {
            completed += 1
            if (completed !== files.length) return

            setSelectedFiles((prev) => [...prev, ...preparedFiles.filter(Boolean)])
        }

        files.forEach((file, index) => {
            const isVideo = file?.type?.startsWith('video/')
            const isImageType = isImage('', file?.name)
            const baseFile = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                name: file.name,
                type: file.type,
                file,
                documentTypeId: null,
                isImage: isImageType,
                isVideo,
                preview: isVideo ? URL.createObjectURL(file) : '',
                documentBase64: null,
                uploadStatus: 'pending',
                uploadProgress: 0,
            }

            if (isVideo) {
                preparedFiles[index] = baseFile
                finalize()
                return
            }

            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                const dataUrl = reader.result || ''
                const commaIndex = dataUrl.indexOf(',')
                preparedFiles[index] = {
                    ...baseFile,
                    preview: dataUrl,
                    documentBase64: commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : null,
                }
                finalize()
            }
            reader.onerror = () => {
                preparedFiles[index] = baseFile
                finalize()
            }
        })
    }

    const updateGcsUploadProgress = (fileId, status, progress) => {
        setSelectedFiles((prev) => prev.map((file) => (
            file.id === fileId
                ? { ...file, uploadStatus: status, uploadProgress: progress }
                : file
        )))
    }

    // Builds a stream body for GCS upload and updates progress as chunks are uploaded.
    const buildGcsUploadBody = (file, fileId) => {
        if (!file?.stream || typeof ReadableStream === 'undefined' || !file.size) {
            return file
        }

        const reader = file.stream().getReader()
        let uploadedBytes = 0

        return new ReadableStream({
            async pull(controller) {
                const { done, value } = await reader.read()
                if (done) {
                    controller.close()
                    return
                }

                uploadedBytes += value.byteLength
                const progress = Math.min(100, Math.round((uploadedBytes / file.size) * 100))
                updateGcsUploadProgress(fileId, 'uploading', progress)
                controller.enqueue(value)
            },
            cancel(reason) {
                reader.cancel(reason)
            },
        })
    }

    const uploadFileToGcs = async (selectedFile) => {
        const contentType = selectedFile.type || 'application/octet-stream'
        updateGcsUploadProgress(selectedFile.id, 'uploading', 0)

        let gcsResponse = null
        try {
            gcsResponse = await generateGcsUploadUrl({
                variables: {
                    input: {
                        folder: 'incidents',
                        contentType,
                    },
                },
            })
        } catch (mutationError) {
            throw new Error(mutationError?.message || 'Unable to generate upload URL.')
        }

        const gcsData = gcsResponse?.data?.generateGcsUploadUrl
        if (!gcsData?.ok || !gcsData?.uploadUrl || !gcsData?.blobPath) {
            const message = gcsData?.errors?.map((e) => e.messages.join(', ')).join('; ')
            throw new Error(message || 'Unable to generate upload URL.')
        }

        const body = buildGcsUploadBody(selectedFile.file, selectedFile.id)
        const requestOptions = {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body,
        }

        if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) {
            requestOptions.duplex = 'half'
        }

        const uploadResponse = await fetch(gcsData.uploadUrl, requestOptions)
        if (!uploadResponse.ok) {
            let serverMessage = ''
            try {
                serverMessage = await uploadResponse.text()
            } catch (e) {
                serverMessage = ''
            }
            const responseMessage = serverMessage ? ` - ${serverMessage.slice(0, 200)}` : ''
            throw new Error(`File upload failed (${uploadResponse.status}).${responseMessage}`)
        }

        let saveDocumentResponse
        try {
            saveDocumentResponse = await createIncidentDocument({
                variables: {
                    input: {
                        incidentId,
                        documentType: selectedFile.documentTypeId,
                        blobPath: gcsData.blobPath,
                        notes: notes || '',
                        slug: resolvedSlug,
                    },
                },
            })
        } catch (mutationError) {
            throw new Error(mutationError?.message || 'Error uploading document.')
        }

        if (!saveDocumentResponse?.data?.createIncidentDocument?.ok) {
            const message = saveDocumentResponse?.data?.createIncidentDocument?.errors?.map((e) => e.messages.join(', ')).join('; ')
            throw new Error(message || 'Error uploading document.')
        }

        updateGcsUploadProgress(selectedFile.id, 'uploaded', 100)
    }

    const uploadFilesToGcsWithConcurrency = async (files, failedFiles, successfulFileIds) => {
        let index = 0

        // Each worker picks the next non-image file and uploads it until all files are processed.
        const processUploadQueue = async () => {
            while (index < files.length) {
                const currentIndex = index
                index += 1
                const file = files[currentIndex]

                try {
                    await uploadFileToGcs(file)
                    successfulFileIds.push(file.id)
                } catch (uploadError) {
                    updateGcsUploadProgress(file.id, 'error', file.uploadProgress || 0)
                    failedFiles.push({
                        id: file.id,
                        name: file.name,
                        message: uploadError?.message || 'File upload failed.',
                    })
                }
            }
        }

        const workerCount = Math.min(2, files.length)
        if (workerCount <= 1) {
            await processUploadQueue()
            return
        }
        // Start multiple workers to process the GCS upload queue concurrently.
        await Promise.all([processUploadQueue(), processUploadQueue()])
    }

    const handleUpload = async () => {
        setError(null)

        if (isClaimCategory && !selectedClaimType?.value) {
            setError('Please select claim type.')
            return
        }

        if (needsTargetSelection && !selectedTarget?.value) {
            setError('Please select where this document belongs.')
            return
        }

        if (!resolvedSlug) {
            setError('Unable to resolve document destination.')
            return
        }

        if (!selectedFiles.length) {
            setError('Please select at least one file to upload.')
            return
        }

        const filesWithoutType = selectedFiles.filter((file) => !file.documentTypeId)
        if (filesWithoutType.length) {
            setError('Please select a document type for each file.')
            return
        }

        setIsUploading(true)

        const successfulFileIds = []
        const failedFiles = []
        const documentsForBulkCreate = []

        try {
            const imageFiles = selectedFiles.filter((file) => file.isImage)
            const gcsFiles = selectedFiles.filter((file) => !file.isImage)

            if (imageFiles.length) {
                const imageFileIds = new Set(imageFiles.map((file) => file.id))
                setSelectedFiles((prev) => prev.map((file) => (
                    imageFileIds.has(file.id)
                        ? { ...file, uploadStatus: 'uploading', uploadProgress: 50 }
                        : file
                )))
                imageFiles.forEach((file) => {
                    if (!file.documentBase64) {
                        setSelectedFiles((prev) => prev.map((currentFile) => (
                            currentFile.id === file.id
                                ? { ...currentFile, uploadStatus: 'error', uploadProgress: 100 }
                                : currentFile
                        )))
                        failedFiles.push({ id: file.id, name: file.name, message: 'Could not read file.' })
                        return
                    }

                    documentsForBulkCreate.push({
                        fileId: file.id,
                        name: file.name,
                        payload: {
                            documentType: file.documentTypeId,
                            document: file.documentBase64,
                            notes: notes || '',
                        },
                    })
                })
            }

            if (gcsFiles.length) {
                setSelectedFiles((prev) => prev.map((file) => (
                    !file.isImage ? { ...file, uploadStatus: 'pending', uploadProgress: 0 } : file
                )))
                await uploadFilesToGcsWithConcurrency(gcsFiles, failedFiles, successfulFileIds)
            }

            if (documentsForBulkCreate.length) {
                let bulkResponse
                try {
                    bulkResponse = await createBulkIncidentDocument({
                        variables: {
                            input: {
                                incidentId,
                                documents: documentsForBulkCreate.map((doc) => doc.payload),
                                slug: resolvedSlug,
                                mergeImages: true,
                            },
                        },
                    })
                } catch (mutationError) {
                    const message = mutationError?.message || 'Error uploading document.'
                    documentsForBulkCreate.forEach((doc) => {
                        failedFiles.push({ id: doc.fileId, name: doc.name, message })
                    })
                }

                if (bulkResponse && !bulkResponse?.data?.createBulkIncidentDocument?.ok) {
                    const message = bulkResponse?.data?.createBulkIncidentDocument?.errors?.map((e) => e.messages.join(', ')).join('; ')
                    documentsForBulkCreate.forEach((doc) => {
                        failedFiles.push({ id: doc.fileId, name: doc.name, message: message || 'Error uploading document.' })
                    })
                }

                if (bulkResponse?.data?.createBulkIncidentDocument?.ok) {
                    const uploadedIds = new Set(documentsForBulkCreate.map((doc) => doc.fileId))
                    setSelectedFiles((prev) => prev.map((file) => (
                        uploadedIds.has(file.id)
                            ? { ...file, uploadStatus: 'uploaded', uploadProgress: 100 }
                            : file
                    )))
                    documentsForBulkCreate.forEach((doc) => {
                        successfulFileIds.push(doc.fileId)
                    })
                }
            }

            if (!failedFiles.length) {
                clearAllFiles()
                setNotes('')
                toggle()
                if (refetchQuery) refetchQuery()
                return
            }

            const failedIds = new Set(failedFiles.map((file) => file.id))

            const firstFailureMessage = failedFiles[0]?.message

            if (successfulFileIds.length > 0) {
                setError(`${successfulFileIds.length} file(s) uploaded, ${failedFiles.length} failed. ${firstFailureMessage || 'Please retry failed files.'}`)
            } else {
                setError(firstFailureMessage || `Upload failed for ${failedFiles.length} file(s). Please retry.`)
            }

            setSelectedFiles((prev) => {
                prev.forEach((file) => {
                    if (!failedIds.has(file.id) && file.isVideo && file.preview?.startsWith('blob:')) {
                        URL.revokeObjectURL(file.preview)
                    }
                })

                return prev
                    .filter((file) => failedIds.has(file.id))
                    .map((file) => ({ ...file, uploadStatus: 'error', uploadProgress: 100 }))
            })
        } catch (uploadError) {
            setError(uploadError.message || 'Error uploading document.')
            setSelectedFiles((prev) => prev.map((file) => (
                file.uploadStatus === 'uploading'
                    ? { ...file, uploadStatus: 'error', uploadProgress: 100 }
                    : file
            )))
        } finally {
            setIsUploading(false)
        }
    }

    const handleModalClosed = () => {
        setError(null)
        clearAllFiles()
        setNotes('')
        setSelectedTarget(null)
        setSelectedClaimType(null)
    }

    const handleFileChange = (e) => {
        if (isUploading) return
        processFiles(Array.from(e.target.files))
        e.target.value = null
    }

    const handleDrop = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
        processFiles(Array.from(e.dataTransfer.files))
    }

    const handleDragOver = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(true)
    }

    const handleDragLeave = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
    }

    const handleAreaClick = () => {
        if (isUploading) return
        fileInputRef.current?.click()
    }

    const removeFile = (index) => {
        if (isUploading) return

        setSelectedFiles((prev) => {
            const removed = prev[index]
            if (removed?.isVideo && removed.preview?.startsWith('blob:')) {
                URL.revokeObjectURL(removed.preview)
            }
            return prev.filter((_, i) => i !== index)
        })
    }

    const updateFileDocumentType = (fileId, option) => {
        if (isUploading) return
        setSelectedFiles((prev) => prev.map((file) => (
            file.id === fileId
                ? { ...file, documentTypeId: option?.value || null }
                : file
        )))
    }

    const openFilePreview = (file) => {
        const previewUrl = file?.preview || (file?.file ? URL.createObjectURL(file.file) : null)
        if (!previewUrl) return
        setPreviewAttachment({
            document: file?.name || "Document",
            documentType: file?.name || "Document",
            url: previewUrl,
            isTempUrl: !file?.preview && previewUrl?.startsWith("blob:")
        })
    }

    const closePreview = () => {
        if (previewAttachment?.isTempUrl && previewAttachment?.url) {
            URL.revokeObjectURL(previewAttachment.url)
        }
        setPreviewAttachment(null)
    }

    return (
        <Modal isOpen={isOpen} onClosed={handleModalClosed} className="incident-document-modal" keyboard={!isUploading} backdrop={isUploading ? 'static' : true}>
            <ModalHeader>Upload Document</ModalHeader>
            <ModalBody>
                {previewAttachment && (
                    <AttachmentViewer
                        open={true}
                        attachment={previewAttachment}
                        handleClose={closePreview}
                    />
                )}
                {error && <Row className="mb-2"><Col><Alert color="danger">{error}</Alert></Col></Row>}
                {isClaimCategory && (
                    <Row className="mb-3">
                        <Col>
                            <FormGroup>
                                <Label>Claim Type</Label>
                                <Select
                                    options={claimTypeOptions}
                                    value={selectedClaimType}
                                    onChange={(option) => {
                                        setSelectedClaimType(option)
                                        setSelectedTarget(null)
                                    }}
                                    isLoading={claimTypeLoading}
                                    isDisabled={isUploading || claimTypeLoading}
                                    placeholder="Select claim type..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                )}
                {needsTargetSelection && (
                    <Row className="mb-3">
                        <Col>
                            <FormGroup>
                                <Label>{documentCategory === "PARTY" ? "Party" : "Claim"}</Label>
                                <Select
                                    options={targetOptions}
                                    value={selectedTarget}
                                    onChange={setSelectedTarget}
                                    isLoading={targetsLoading}
                                    isDisabled={isUploading || targetsLoading || (isClaimCategory && !selectedClaimType?.value)}
                                    placeholder={documentCategory === "PARTY" ? "Select party..." : "Select claim..."}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                )}
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>File</Label><br />
                            <div
                                className={`drag-drop-area${isDragActive ? ' drag-drop-active' : ''}`}
                                onClick={handleAreaClick}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                {isDragActive ? <span className="drag-drop-anim">Release to upload files</span> : <span>Drag & drop files here or click to select</span>}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                    accept=".pdf,image/*,video/*"
                                    multiple
                                    disabled={isUploading}
                                />
                            </div>
                            <div className="selected-files-preview">
                                {selectedFiles.map((file, index) => (
                                    <div key={file.id} className="file-preview-item">
                                        <Button
                                            type="button"
                                            className="file-remove-btn"
                                            color="link"
                                            onClick={() => removeFile(index)}
                                            disabled={isUploading}
                                            title="Remove file"
                                        >
                                            <i className="fa fa-times-circle" aria-hidden="true" />
                                        </Button>
                                        <div className="file-preview-row">
                                            <button
                                                type="button"
                                                className="file-meta-col file-meta-preview-btn"
                                                title={file.name}
                                                onClick={() => openFilePreview(file)}
                                                disabled={isUploading}
                                            >
                                                <i className={getFileIconClass(file)} aria-hidden="true" />
                                                <span className="file-preview-name">{file.name}</span>
                                            </button>
                                            <div className="file-type-select-wrap">
                                                <Select
                                                    className="file-type-select"
                                                    classNamePrefix="file-type-select"
                                                    options={documentTypeOptions}
                                                    value={documentTypeOptions.find((option) => option.value === file.documentTypeId) || null}
                                                    onChange={(option) => updateFileDocumentType(file.id, option)}
                                                    isDisabled={typesLoading || isUploading || (isClaimCategory && !selectedClaimType?.value)}
                                                    placeholder="Select type..."
                                                />
                                            </div>
                                        </div>
                                        {file.uploadStatus !== 'pending' && (
                                            <div className="file-upload-progress-wrapper">
                                                <div
                                                    className={`file-upload-progress-bar status-${file.uploadStatus || 'pending'}`}
                                                    style={{ width: `${file.uploadProgress || 0}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>Notes</Label>
                            <Input
                                type="textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                disabled={isUploading}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button className="incident-document-modal-secondary-btn" onClick={toggle} disabled={isUploading}>Cancel</Button>
                <Button className="incident-document-modal-primary-btn" onClick={handleUpload} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default UploadIncidentDocumentModal
